import type { ProductDraftErrors, ProductDraftFields } from '../domain/product-form';
import { fieldLabels, fieldLimits } from '../domain/product-form';

import {
  cloneProductFields,
  emptyProductDraft,
  productDemoCategories,
  unsafeProductText,
  validateProductTemporaryFile,
} from '../domain/product-form';

import type { ProviderProduct } from './provider-data';

import { providerProducts } from './provider-data';

/** These UI-only structures never issue Supplier/ProductModel/LocalAsset identities. */
export type DemoProductDraftId = `demo-product-${string}`;

/**
 * Runtime narrowing back to the DEMO identifier brand.
 *
 * <p>The shared editor's context carries a plain string id, so the SYN LIVE path can hand it a
 * durable 06 product id. The DEMO store must still prove it is holding a demo draft id before it
 * writes, rather than casting one into the other.
 */
export function isDemoProductDraftId(value: string): value is DemoProductDraftId {
  return value.startsWith('demo-product-');
}

export type ProductDemoStatus = 'DRAFT' | 'PENDING_REVIEW' | 'CHANGES_REQUESTED';

export interface ProductDemoDraft {
  demoProductDraftId: DemoProductDraftId;
  fields: ProductDraftFields;
  baseline: ProductDraftFields | null;
  baselineFixtureId: string | null;
  version: 'v1' | 'v2';
  status: ProductDemoStatus;
  feedback: string;
  origin: 'SESSION' | 'STATIC_DEMO';
}

export const productDemoStatusLabels: Record<ProductDemoStatus, string> = {
  DRAFT: '草稿 · DRAFT · DEMO',
  PENDING_REVIEW: '待审核 · PENDING_REVIEW · DEMO',
  CHANGES_REQUESTED: '退回修改 · CHANGES_REQUESTED · DEMO',
};

export function productFieldsFromFixture(product: ProviderProduct): ProductDraftFields {
  return {
    ...emptyProductDraft(),
    name: product.name,
    model: product.model,
    category: product.category,
    purpose: product.purpose,
    features: product.features,
    claims: product.features,
    limits: '未核实技术能力、认证和现场适用性；不代表作业授权。',
    scenes: product.scenes,
    source: 'W1 06 独立产品演示 fixture / 设计包素材，非正式 Owner 版本',
  };
}

export function validateProductDraft(
  fields: ProductDraftFields,
  records: readonly ProductDemoDraft[],
  currentId: DemoProductDraftId | null,
  submitting: boolean,
  revising: boolean,
): ProductDraftErrors {
  const errors: ProductDraftErrors = {};
  for (const [field, limit] of Object.entries(fieldLimits)) {
    const key = field as keyof typeof fieldLimits;
    const value = fields[key];
    if (value.length > limit) errors[key] = `${fieldLabels[key]}最多 ${limit} 字`;
    else if (unsafeProductText(value))
      errors[key] = '不接受 HTML、危险地址或控制字符，请使用普通文本';
  }
  if (!fields.name.trim()) errors.name = '请填写产品名称';
  if (fields.category && !productDemoCategories.includes(fields.category))
    errors.category = '请选择可用演示类别';
  const model = fields.model.trim().toLowerCase();
  if (
    !revising &&
    model &&
    model !== 'unknown' &&
    providerProducts.some((product) => product.model.toLowerCase() === model)
  )
    errors.model = '历史演示产品已有此型号，请从原详情编辑拟议 v2';
  if (
    model &&
    model !== 'unknown' &&
    records.some(
      (record) =>
        record.demoProductDraftId !== currentId &&
        record.fields.model.trim().toLowerCase() === model,
    )
  )
    errors.model = '本端已有相同演示型号，请打开现有草稿修订';
  if (fields.model && model !== 'unknown' && !/^[a-z0-9][a-z0-9._ -]*$/i.test(fields.model.trim()))
    errors.model = '型号仅接受字母、数字、空格、点、下划线或连字符；未确定可填 UNKNOWN';
  if (submitting) {
    for (const key of [
      'manufacturer',
      'category',
      'purpose',
      'claims',
      'limits',
      'source',
    ] as const)
      if (!fields[key].trim()) errors[key] = `模拟提交前请填写${fieldLabels[key]}`;
    if (!model || model === 'unknown') errors.model = 'UNKNOWN 可保存草稿，模拟提交需要明确型号';
    if (revising && !fields.reason.trim()) errors.reason = '修订版本模拟提交前请说明修改原因';
  }
  if (fields.parameters.length > 30) errors.parameters = '最多 30 个演示技术参数';
  const keys = new Set<string>();
  fields.parameters.forEach((parameter, index) => {
    const normalized = parameter.key.trim().toLowerCase();
    if (!normalized) errors[`parameter-${index}`] = '参数名称不能为空';
    else if (keys.has(normalized))
      errors[`parameter-${index}`] = '参数名称重复（忽略大小写和首尾空格）';
    else if (!parameter.value.trim()) errors[`parameter-${index}`] = '参数值不能为空';
    else if (
      parameter.key.length > 60 ||
      parameter.value.length > 160 ||
      parameter.unit.length > 30
    )
      errors[`parameter-${index}`] = '参数名称 / 值 / 单位最多 60 / 160 / 30 字';
    else if (
      [parameter.key, parameter.value, parameter.unit].some((value) => unsafeProductText(value))
    )
      errors[`parameter-${index}`] = '参数不接受 HTML 或危险文本';
    keys.add(normalized);
  });
  if (fields.files.length > 8) errors.files = '最多选择 8 个临时文件名';
  const fileNames = new Set<string>();
  for (const file of fields.files) {
    const fileError = validateProductTemporaryFile(file);
    if (fileError) errors.files = fileError;
    const key = file.name.trim().toLowerCase();
    if (fileNames.has(key)) errors.files = '临时文件名重复';
    fileNames.add(key);
  }
  return errors;
}

export interface ProductDraftOperation {
  records: readonly ProductDemoDraft[];
  errors: ProductDraftErrors;
  message: string;
}

export function saveProductDemoDraft(
  records: readonly ProductDemoDraft[],
  id: DemoProductDraftId,
  fields: ProductDraftFields,
  baseline: ProductDraftFields | null,
  fixtureId: string | null,
  submitting: boolean,
): ProductDraftOperation {
  const previous = records.find((record) => record.demoProductDraftId === id);
  if (previous?.status === 'PENDING_REVIEW')
    return { records, errors: { state: '待审核演示资料只读；重复提交或编辑被拒绝' }, message: '' };
  const errors = validateProductDraft(fields, records, id, submitting, baseline !== null);
  if (Object.keys(errors).length) return { records, errors, message: '' };
  const record: ProductDemoDraft = {
    demoProductDraftId: id,
    fields: cloneProductFields(fields),
    baseline: baseline ? cloneProductFields(baseline) : null,
    baselineFixtureId: fixtureId,
    version: baseline ? 'v2' : 'v1',
    status: submitting ? 'PENDING_REVIEW' : 'DRAFT',
    feedback: previous?.feedback ?? '',
    origin: previous?.origin ?? 'SESSION',
  };
  return {
    records: previous
      ? records.map((item) => (item.demoProductDraftId === id ? record : item))
      : [...records, record],
    errors: {},
    message: submitting
      ? '已模拟提交审核 · 未发送至 08 · 仅本端内存'
      : '已保存本次会话草稿 · 刷新即丢失 · 未保存服务器',
  };
}

export function deleteProductDemoDraft(
  records: readonly ProductDemoDraft[],
  id: DemoProductDraftId,
): ProductDraftOperation {
  const record = records.find((item) => item.demoProductDraftId === id);
  if (!record || record.status !== 'DRAFT')
    return {
      records,
      errors: { state: '只能删除本端草稿；待审核及退回样例不能直接删除' },
      message: '',
    };
  return {
    records: records.filter((item) => item.demoProductDraftId !== id),
    errors: {},
    message: '已移除本端会话草稿 · 不影响正式产品或历史样例',
  };
}

export function createProductDemoSeeds(): ProductDemoDraft[] {
  const v1: ProductDraftFields = {
    ...emptyProductDraft(),
    name: '消防灭火机器人（反馈演示）',
    model: 'XT-100-DEMO',
    category: '灭火装备',
    purpose: '建筑火灾用途声明示例',
    claims: '远程观察与信息回传设计',
    limits: '技术真实性和认证未知',
    source: '06 独立受控退回 fixture · 非 08 回填',
  };
  return [
    {
      demoProductDraftId: 'demo-product-returned-xt100',
      fields: {
        ...cloneProductFields(v1),
        purpose: '补充建筑消防用途声明（拟议 v2）',
        reason: '根据演示反馈补充用途与安全限制',
      },
      baseline: cloneProductFields(v1),
      baselineFixtureId: null,
      version: 'v2',
      status: 'CHANGES_REQUESTED',
      feedback: '演示意见：补充技术参数来源、安全限制及适用场景边界。未向真实厂商发送。',
      origin: 'STATIC_DEMO',
    },
  ];
}
